import { InfoIcon } from '@phosphor-icons/react';
import { Card, CardContent } from '../../components/ui/card';
import CustomTooltip from '../../components/tooltip/CustomTooltip';

const QuestionAnalytics = ({ currentSolution, comparisonData }) => {
  return (
    <div className="mb-4 flex flex-col md:flex-row gap-4 md:gap-2 justify-between w-full select-none">
      {/* TIME SPENT + AVERAGE TIME */}
      <Card className="p-0 m-0 rounded-xl border border-border shadow-none flex-[2]">
        <CardContent className="flex text-center p-0 divide-x divide-border">
          {/* TIME SPENT BLOCK */}
          <div className="flex-1 flex flex-col divide-y divide-border">
            <div className="bg-[var(--q3-surface-dim)] flex flex-col items-center justify-center p-1 rounded-tl-xl h-[52.5px]">
              <div className="text-xs text-muted-foreground font-bold uppercase">Time Spent</div>
              <div className="text-[8px] md:text-[10px] text-muted-foreground">Out of alotted time</div>
            </div>
            <div className="flex items-center justify-center p-4 bg-[var(--q3-surface-default)] rounded-bl-xl">
              <div className="text-[var(--q3-orange-normal)] font-medium text-[16px]">
                {currentSolution?.timeTaken ?? '--'}s
              </div>
              {currentSolution?.questionId?.approximateTimeRequired && (
                <div className="text-[var(--q3-neutral-light)] text-xs">
                  &nbsp;/ {currentSolution.questionId.approximateTimeRequired}s
                </div>
              )}
            </div>
          </div>

          {/* AVERAGE TIME BLOCK */}
          <div className="flex-1 flex flex-col divide-y divide-border">
            <div className="bg-[var(--q3-surface-dim)] p-1 flex items-center justify-center h-[52.5px] rounded-tr-xl">
              <div className="text-xs text-muted-foreground font-bold uppercase mr-1">Average Time</div>
              <CustomTooltip text={'Typical time taken by students to solve this question'}>
                <InfoIcon size={16} className="text-[var(--q3-accent-normal)] cursor-pointer" />
              </CustomTooltip>
            </div>
            <div className="text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] rounded-br-xl text-[16px] font-medium flex items-center justify-center p-4">
              {comparisonData?.timeTaken ? `${comparisonData.timeTaken}s` : '--'}
            </div>
          </div>
        </CardContent>
      </Card>
      {/* ATTEMPTED RIGHT + WRONG */}
      <Card className="p-0 m-0 rounded-xl border border-border shadow-none flex-[2]">
        <CardContent className="flex text-center p-0 divide-x divide-border">
          {/* ATTEMPTED RIGHT */}
          <div className="flex-1 flex flex-col divide-y divide-border">
            <div className="bg-[var(--q3-surface-dim)] flex flex-col items-center justify-center p-1 rounded-tl-xl h-[52.5px]">
              <div className="text-xs text-muted-foreground font-bold uppercase">Got it right</div>
              <div className="text-[8px] md:text-[10px] text-muted-foreground">Out of those who attempted qs</div>
            </div>
            <div className="flex items-center justify-center p-4 bg-[var(--q3-surface-default)] rounded-bl-xl">
              <div className="text-[var(--q3-green-normal)] font-medium text-[16px]">
                {comparisonData?.correctPercentage ? `${comparisonData.correctPercentage}%` : '--'}
              </div>
            </div>
          </div>

          {/* ATTEMPTED WRONG */}
          <div className="flex-1 flex flex-col divide-y divide-border">
            <div className="bg-[var(--q3-surface-dim)] flex flex-col items-center justify-center p-1 rounded-tr-xl h-[52.5px]">
              <div className="text-xs text-muted-foreground font-bold uppercase">Got it wrong</div>
              <div className="text-[8px] md:text-[10px] text-muted-foreground">Out of those who attempted qs</div>
            </div>
            <div className="flex items-center justify-center p-4 bg-[var(--q3-surface-default)] rounded-br-xl">
              <div className="text-[var(--q3-danger-normal)] font-medium text-[16px]">
                {comparisonData?.incorrectPercentage ? `${comparisonData.incorrectPercentage}%` : '--'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="p-0 m-0 rounded-xl border border-border shadow-none flex-[2] md:hidden">
        <CardContent className="flex text-center p-0 divide-x divide-border">
          {/* ATTEMPTED */}
          <div className="flex-1 flex flex-col divide-y divide-border">
            <div className="bg-[var(--q3-surface-dim)] flex flex-col items-center justify-center p-1 rounded-tl-xl h-[52.5px]">
              <div className="text-xs text-muted-foreground font-bold uppercase">Attempted</div>
            </div>
            <div className="flex items-center justify-center p-4 bg-[var(--q3-surface-default)] rounded-bl-xl">
              <div className="text-[var(--q3-green-normal)] font-medium text-[16px]">
                {(comparisonData?.notAnswered ?? comparisonData?.notAnswered === 0)
                  ? `${(100 - comparisonData.notAnswered).toFixed(2)}%`
                  : '--'}
              </div>
            </div>
          </div>

          {/* DIDN'T ATTEMPT */}
          <div className="flex-1 flex flex-col divide-y divide-border">
            <div className="bg-[var(--q3-surface-dim)] flex flex-col items-center justify-center p-1 rounded-tr-xl h-[52.5px]">
              <div className="text-xs text-muted-foreground font-bold uppercase">Skipped</div>
            </div>
            <div className="flex items-center justify-center p-4 bg-[var(--q3-surface-default)] rounded-br-xl">
              <div className="text-[var(--q3-danger-normal)] font-medium text-[16px]">
                {comparisonData?.notAnswered ? `${comparisonData.notAnswered}%` : '--'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* DIDN'T ATTEMPT */}
      <Card className="p-0 m-0 rounded-xl border border-border shadow-none hidden md:flex md:flex-[1]">
        <CardContent className="flex text-center p-0">
          <div className="flex-1 flex flex-col divide-y divide-border">
            <div className="bg-[var(--q3-surface-dim)] flex flex-col items-center justify-center p-1 rounded-t-xl h-[52.5px]">
              <div className="text-xs text-muted-foreground font-bold uppercase">Skipped</div>
            </div>
            <div className="flex items-center justify-center p-4 bg-[var(--q3-surface-default)] rounded-b-xl">
              <div className="text-[var(--q3-neutral-default)] font-medium text-[16px]">
                {comparisonData?.notAnswered ? `${comparisonData.notAnswered}%` : '--'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuestionAnalytics;
